import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { Plus, Trash2, Edit2, ArrowRight, ExternalLink, Info } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RedirectsManager = () => {
  const { toast } = useToast();
  const [redirects, setRedirects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRedirect, setEditingRedirect] = useState(null);
  const [formData, setFormData] = useState({
    from_path: '',
    to_path: '',
    redirect_type: 301,
  });

  useEffect(() => {
    fetchRedirects();
  }, []);

  const fetchRedirects = async () => {
    try {
      const response = await axios.get(`${API}/admin/redirects`, { withCredentials: true });
      setRedirects(response.data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to fetch redirects',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.from_path || !formData.to_path) {
      toast({
        title: 'Error',
        description: 'Both "From" and "To" paths are required',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingRedirect) {
        await axios.put(`${API}/admin/redirects/${editingRedirect.id}`, formData, { withCredentials: true });
        toast({ title: 'Success', description: 'Redirect updated successfully' });
      } else {
        await axios.post(`${API}/admin/redirects`, formData, { withCredentials: true });
        toast({ title: 'Success', description: 'Redirect created successfully' });
      }
      setShowDialog(false);
      setEditingRedirect(null);
      setFormData({ from_path: '', to_path: '', redirect_type: 301 });
      fetchRedirects();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save redirect',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this redirect?')) return;

    try {
      await axios.delete(`${API}/admin/redirects/${id}`, { withCredentials: true });
      toast({ title: 'Success', description: 'Redirect deleted successfully' });
      fetchRedirects();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete redirect',
        variant: 'destructive',
      });
    }
  };

  const openEditDialog = (redirect) => {
    setEditingRedirect(redirect);
    setFormData({
      from_path: redirect.from_path,
      to_path: redirect.to_path,
      redirect_type: redirect.redirect_type,
    });
    setShowDialog(true);
  };

  const openCreateDialog = () => {
    setEditingRedirect(null);
    setFormData({ from_path: '', to_path: '', redirect_type: 301 });
    setShowDialog(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Redirects Manager</h1>
          <p className="text-gray-500 mt-1">Manage URL redirects for SEO and user experience</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-redirect-btn">
          <Plus size={20} className="mr-2" />
          Add Redirect
        </Button>
      </div>

      {/* Info Card */}
      <Card className="p-4 mb-6 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Info size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">How redirects work:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-700">
              <li><strong>301 (Permanent)</strong> - Use for pages that have permanently moved. Search engines will update their index.</li>
              <li><strong>302 (Temporary)</strong> - Use for temporary redirects. Search engines keep the original URL indexed.</li>
              <li>Enter paths without the domain (e.g., <code className="bg-blue-100 px-1 rounded">/old-page</code> → <code className="bg-blue-100 px-1 rounded">/new-page</code>)</li>
              <li>For external URLs, include the full URL in "To" path (e.g., <code className="bg-blue-100 px-1 rounded">https://example.com</code>)</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Redirects List */}
      {redirects.length === 0 ? (
        <Card className="p-8 text-center">
          <ArrowRight size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">No redirects configured</h3>
          <p className="text-gray-500 mb-4">Add your first redirect to manage URL changes</p>
          <Button onClick={openCreateDialog}>
            <Plus size={20} className="mr-2" />
            Add Your First Redirect
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {redirects.map((redirect) => (
            <Card key={redirect.id} className="p-4" data-testid={`redirect-${redirect.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm truncate max-w-[200px]" title={redirect.from_path}>
                      {redirect.from_path}
                    </code>
                    <ArrowRight size={16} className="text-gray-400 flex-shrink-0" />
                    <code className="bg-green-100 px-2 py-1 rounded text-sm truncate max-w-[200px]" title={redirect.to_path}>
                      {redirect.to_path}
                    </code>
                    {redirect.to_path.startsWith('http') && (
                      <ExternalLink size={14} className="text-gray-400 flex-shrink-0" />
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium flex-shrink-0 ${
                    redirect.redirect_type === 301 
                      ? 'bg-purple-100 text-purple-700' 
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {redirect.redirect_type}
                  </span>
                  <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
                    {formatDate(redirect.created_at)}
                  </span>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditDialog(redirect)}
                    data-testid={`edit-redirect-${redirect.id}`}
                  >
                    <Edit2 size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(redirect.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-redirect-${redirect.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingRedirect ? 'Edit Redirect' : 'Add New Redirect'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                From Path <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.from_path}
                onChange={(e) => setFormData({ ...formData, from_path: e.target.value })}
                placeholder="/old-page-url"
                data-testid="redirect-from-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                The old URL path (without domain)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                To Path <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.to_path}
                onChange={(e) => setFormData({ ...formData, to_path: e.target.value })}
                placeholder="/new-page-url or https://..."
                data-testid="redirect-to-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                The new URL path or full external URL
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Redirect Type
              </label>
              <Select
                value={String(formData.redirect_type)}
                onValueChange={(value) => setFormData({ ...formData, redirect_type: parseInt(value) })}
              >
                <SelectTrigger data-testid="redirect-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="301">301 - Permanent Redirect</SelectItem>
                  <SelectItem value="302">302 - Temporary Redirect</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                301 for permanent moves, 302 for temporary
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} data-testid="save-redirect-btn">
              {editingRedirect ? 'Update' : 'Create'} Redirect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RedirectsManager;
